import { ethers } from "ethers";

// --- Constants --------------------------------------------------------------
const WORMHOLE_ID = { BNB: 4, BASE: 30 } as const;

const TOKENS = {
  BNB:  "0x241F21dF529C05289A00dAfeCEa10139A287cDCa",
  BASE: "0x7d03acf64a1a64bff9d24850011cca93c78a22e7",
} as const;

const WITH_REFERRER = "0x412f30e9f8B4a1e99eaE90209A6b00f5C3cc8739";
const TBR = {
  BNB:  "0x2513515340fF71DD5AF02fC1BdB9615704d91524",
  BASE: "0xD8B736EF27Fc997b1d00F22FE37A58145D3BDA07",
} as const;

const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function allowance(address,address) view returns (uint256)",
  "function approve(address,uint256) returns (bool)",
];

const WITH_REFERRER_ABI = [
  "function transferTokensWithRelay(" +
    "address tokenBridgeRelayer," +
    "address token," +
    "uint256 amount," +
    "uint16 targetChain," +
    "bytes32 targetRecipient," +
    "uint32 nonce," +
    "bytes32 dstTransferRecipient," +
    "bytes32 dstExecutionAddress," +
    "uint256 executionAmount," +
    "address refundAddr," +
    "bytes signedQuoteBytes," +
    "bytes relayInstructions," +
    "tuple(uint16 dbps, address payee) feeArgs" +
  ") payable returns (uint64)",
];

const toBytes32Address = (a: string) =>
  ethers.zeroPadValue(ethers.getAddress(a), 32);

// --- Token Bridge 8-decimal normalization helpers ----------------------------
// Wormhole Token Bridge normalizes all amounts to 8 decimals, causing truncation
// for tokens with more than 8 decimals.

function normalizeTo8(amount: bigint, decimals: number): bigint {
  if (decimals <= 8) return amount;
  const factor = 10n ** BigInt(decimals - 8);
  return amount / factor; // floor
}

function denormalizeFrom8(amount8: bigint, decimals: number): bigint {
  if (decimals <= 8) return amount8;
  const factor = 10n ** BigInt(decimals - 8);
  return amount8 * factor;
}

// Fee in "dbps" (tenths of a basis point). Matches the contract's math.
function calcFeeDbps(amount: bigint, dbps: number): bigint {
  // dbps is uint16; divisor = 100000 (because 1 dbps = 0.001%).
  return (amount * BigInt(dbps)) / 100000n;
}

function inferDirectionFromChainId(chainId: bigint): { src: "BNB" | "BASE"; dst: "BNB" | "BASE" } {
  if (chainId === 56n) return { src: "BNB", dst: "BASE" };
  if (chainId === 8453n) return { src: "BASE", dst: "BNB" };
  throw new Error(`Unsupported chainId ${chainId.toString()}`);
}

// --- Types -------------------------------------------------------------------
export interface BridgeQuote {
  signedQuote: string;
  estimatedCost: string;
}

export interface BridgeParams {
  signer: ethers.Signer;
  recipient: string;
  amount: string;
  relayInstructions?: string;
  referrerDbps?: number;
  referrerPayee?: string;
}

export type BridgeDirection = "BNB_TO_BASE" | "BASE_TO_BNB";

export interface EstimationResult {
  direction: BridgeDirection;
  srcDecimals: number;
  dstDecimals: number;
  amountSrcWei: string;
  feeWei: string;
  normalized8: string;
  expectedDstWei: string;
  expectedDstHuman: string;
}

// --- Estimation function -----------------------------------------------------
// Calculates the expected amount received after bridging, accounting for:
// - Referrer fees (dbps)
// - Token Bridge's 8-decimal normalization/truncation
// - Different decimals between source and destination tokens

export async function estimateWelephantReceived(params: {
  signer: ethers.Signer;      // must have provider attached
  amount: string;             // decimal amount in source token units
  referrerDbps?: number;      // FeeArgs.dbps (0 if none)
  // optional overrides if you want to control RPC selection
  bnbRpc?: string;
  baseRpc?: string;
}): Promise<EstimationResult> {
  const provider = params.signer.provider;
  if (!provider) throw new Error("Signer must have an attached provider");

  const { chainId } = await provider.getNetwork();
  const { src, dst } = inferDirectionFromChainId(chainId);

  const srcToken = TOKENS[src];
  const dstToken = TOKENS[dst];

  // Source decimals
  const srcErc20 = new ethers.Contract(srcToken, ERC20_ABI, provider);
  const srcDecimals = Number(await srcErc20.decimals());

  // Destination decimals (use a lightweight public RPC internally)
  const dstRpc =
    dst === "BNB"
      ? (params.bnbRpc ?? "https://bsc-dataseed1.binance.org")
      : (params.baseRpc ?? "https://mainnet.base.org");

  const dstProvider = new ethers.JsonRpcProvider(dstRpc);
  const dstErc20 = new ethers.Contract(dstToken, ERC20_ABI, dstProvider);
  const dstDecimals = Number(await dstErc20.decimals());

  // Convert user amount to source wei
  const amountSrcWei = ethers.parseUnits(params.amount, srcDecimals);

  // Apply referrer fee (if any), then normalize->denormalize to account for Token Bridge 8-dec truncation
  const dbps = params.referrerDbps ?? 0;
  const fee = calcFeeDbps(amountSrcWei, dbps);
  const afterFee = amountSrcWei - fee;

  const normalized8 = normalizeTo8(afterFee, srcDecimals);
  const dstWei = denormalizeFrom8(normalized8, dstDecimals);

  return {
    direction: `${src}_TO_${dst}` as BridgeDirection,
    srcDecimals,
    dstDecimals,
    amountSrcWei: amountSrcWei.toString(),
    feeWei: fee.toString(),
    normalized8: normalized8.toString(),
    expectedDstWei: dstWei.toString(),
    expectedDstHuman: ethers.formatUnits(dstWei, dstDecimals),
  };
}

// --- Quote fetch ------------------------------------------------------------
export async function getSignedQuote(
  src: number,
  dst: number,
  relayInstructions?: string
): Promise<BridgeQuote> {
  const body = {
    srcChain: src,
    dstChain: dst,
    relayInstructions:
      relayInstructions ??
      "0x01000000000000000000000000000f424000000000000000000000000000000000",
  };

  const res = await fetch("https://executor.labsapis.com/v0/quote", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Quote API ${res.status}`);
  return (await res.json()) as BridgeQuote;
}

// --- Helper to get quote for a direction ------------------------------------
export async function getQuoteForDirection(
  direction: BridgeDirection,
  relayInstructions?: string
): Promise<BridgeQuote> {
  const srcChain = direction === "BNB_TO_BASE" ? WORMHOLE_ID.BNB : WORMHOLE_ID.BASE;
  const dstChain = direction === "BNB_TO_BASE" ? WORMHOLE_ID.BASE : WORMHOLE_ID.BNB;
  return getSignedQuote(srcChain, dstChain, relayInstructions);
}

// --- Core function ----------------------------------------------------------
export async function bridgeWelephant(params: BridgeParams) {
  const { signer, recipient, amount } = params;
  if (!signer.provider) throw new Error("Signer must have a provider");

  const { chainId } = await signer.provider.getNetwork();
  let src: "BNB" | "BASE", dst: "BNB" | "BASE";
  if (chainId === 56n) {
    src = "BNB"; dst = "BASE";
  } else if (chainId === 8453n) {
    src = "BASE"; dst = "BNB";
  } else {
    throw new Error(`Unsupported chain id ${chainId}`);
  }

  const tokenAddr = TOKENS[src];
  const token = new ethers.Contract(tokenAddr, ERC20_ABI, signer);
  const decimals: number = await token.decimals();
  const amountWei = ethers.parseUnits(amount, decimals);

  const quote = await getSignedQuote(WORMHOLE_ID[src], WORMHOLE_ID[dst], params.relayInstructions);

  const withRef = new ethers.Contract(WITH_REFERRER, WITH_REFERRER_ABI, signer);
  const owner = await signer.getAddress();

  // approve if needed
  const allowance: bigint = await token.allowance(owner, WITH_REFERRER);
  if (allowance < amountWei) {
    const txA = await token.approve(WITH_REFERRER, amountWei);
    await txA.wait();
  }

  const dstRelayer = TBR[dst];
  const dstRelayerB32 = toBytes32Address(dstRelayer);
  const recipientB32 = toBytes32Address(recipient);
  const targetChain = WORMHOLE_ID[dst];
  const executionAmount = BigInt(quote.estimatedCost);
  const nonce = Math.floor(Math.random() * 2 ** 32);

  const tx = await withRef.transferTokensWithRelay(
    TBR[src],
    tokenAddr,
    amountWei,
    targetChain,
    recipientB32,
    nonce,
    dstRelayerB32,
    dstRelayerB32,
    executionAmount,
    owner,
    quote.signedQuote,
    params.relayInstructions ??
      "0x01000000000000000000000000000f424000000000000000000000000000000000",
    {
      dbps: params.referrerDbps ?? 0,
      payee: params.referrerPayee ?? ethers.ZeroAddress,
    },
    { value: executionAmount }
  );

  return tx.wait();
}

// --- Exports for constants --------------------------------------------------
export { TOKENS, WORMHOLE_ID, WITH_REFERRER, TBR, ERC20_ABI };
